import * as React from 'react';
import classNames from 'classnames';
import { assign as _assign, difference as _difference, find as _find } from 'lodash';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { ConfigurationModel, BuiltInPartsModel, CUT_OUT_TYPES, BuiltInPartsDefaultModel, buildCutOutSpec, CutOut, techSpecs, Configuration } from 'franke-trough-dependencies';

import { requestAPIActions } from 'loopback-react-request';
import { CheckboxPositions } from '../Utils';
import { CutOutDisposalCustom } from './CutOutDisposalCustom';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './Recesses.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';
import CutOutDisposalSelection from './CutOutDisposalSelection';
import ApronRequiredChoice from '../ApronBacksplash/ApronRequiredChoice';
import DepthRequired from '../BasinDimensions/DepthRequired';
import ConsolesOverlapChoice from '../Assemblies/ConsoleOverlapChoice';

export interface CutOutDisposalProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: any;
}

export interface CutOutDisposalState {
  cutOutDisposals: boolean;
  cutOutDisposalsType: null | CUT_OUT_TYPES;
  cutOutDisposalsPosition: null | (CUT_OUT_TYPES | boolean)[];
  cutOutDisposalsApronRequired: boolean;
  cutOutDisposalsCustomSettings: any;
}

export class CutOutDisposalPartial extends React.Component<CutOutDisposalProps, CutOutDisposalState> {
  constructor(props: CutOutDisposalProps) {
    super(props);
    this.state = {
      cutOutDisposals: false,
      cutOutDisposalsType: null,
      cutOutDisposalsPosition: null,
      cutOutDisposalsApronRequired: true,
      cutOutDisposalsCustomSettings: null
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handlePositionChange = this.handlePositionChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      ...this.state,
      cutOutDisposals: this.cutOutDisposals,
      cutOutDisposalsType: this.cutOutDisposalsType,
      cutOutDisposalsPosition: this.cutOutDisposalsPosition || []
    });
  }

  componentDidUpdate(prevProps: CutOutDisposalProps) {
    const configuration = this.configuration;
    const prevConfiguration = prevProps.configuration && prevProps.configuration.result;

    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      setTimeout(() => {
        if (this.cutOutDisposals !== this.state.cutOutDisposals) {
          this.setState({
            ...this.state,
            cutOutDisposals: this.cutOutDisposals,
          });
        }
        if (
          this.cutOutDisposalsType && 
          JSON.stringify(this.cutOutDisposalsType) !== JSON.stringify(this.state.cutOutDisposalsType)
        ) {
          this.setState({
            ...this.state,
            cutOutDisposalsType: this.cutOutDisposalsType, 
          });
        }
        if (
          this.cutOutDisposalsPosition && 
          JSON.stringify(this.cutOutDisposalsPosition) !== JSON.stringify(this.state.cutOutDisposalsPosition)
        ) {
          this.setState({
            ...this.state,
            cutOutDisposalsPosition: this.cutOutDisposalsPosition,
          });
        }
        if (typeof this.cutOutDisposalsApronRequired === 'boolean' && this.cutOutDisposalsApronRequired !== this.state.cutOutDisposalsApronRequired) {
          this.setState({
            ...this.state,
            cutOutDisposalsApronRequired: this.cutOutDisposalsApronRequired,
          });
        }
        // Since adding in this check, there was a weird bug where positions didnt update... I could only fix it with a setTimeout...
        // Is there something I am missing here? I saw this issue previously with CutOutDisposalCustom as well...
        if (
          this.cutOutDisposalsCustomSettings && 
          JSON.stringify(this.cutOutDisposalsCustomSettings) !== JSON.stringify(this.state.cutOutDisposalsCustomSettings)
        ) {
          this.setState({
            ...this.state,
            cutOutDisposalsCustomSettings: this.cutOutDisposalsCustomSettings,
          });
        }
      }, 1);
    }
  }

  public render() {
    const { 
      cutOutDisposals, 
      cutOutDisposalsPosition
    } = this.state;
    const cutOutDisposalsEnabled = this.cutOutDisposalsPositionDefaults && this.cutOutDisposalsPositionDefaults.isEnabled;
    const typesDefaults = this.cutOutDisposalsTypeDefaults;
     
    return (
      <div className="partial-cutout-disposal position-relative pb-4">
        <ConsolesOverlapChoice id="apronOverlap" />
        
        {this.cutOutDisposalsDefaults && this.cutOutDisposalsDefaults.values.map((value, index) => {
          const key = `has_paper_towel_${index}`;

          return (
            <React.Fragment key={key}>
              <div className="col-12">
                <label className={classNames({disabled: this.disabled})}>
                  {!value ? (
                    <span>
                      <FormattedMessage {...MESSAGES.cutOutDisposalsDefaultsWithout} />
                    </span>
                  ) : (
                    <>
                      <span>
                        <FormattedMessage {...MESSAGES.cutOutDisposalsDefaultsWith} />
                      </span>
                      {isConfigurationEditable() && this.requiredApronHeight > 0 && (
                        <ApronRequiredChoice id={`cut_out_disposal`} requiredHeight={this.requiredApronHeight} />
                      )}
                      {isConfigurationEditable() && this.requiredDepth > 0 && (
                        <DepthRequired id={`cut_out_disposal`} requiredDepth={this.requiredDepth} />
                      )}
                    </>
                  )}
                  <input 
                    checked={cutOutDisposals === value}
                    disabled={this.disabled}
                    onChange={this.handleChange}
                    name="cutOutDisposals"
                    type="radio"
                    value={value.toString()}
                  />
                  <span className="checkmark" />
                </label>
              </div>
              {cutOutDisposals && value.toString() === 'true' && (
                <>
                  {typesDefaults && (
                    <div className="pb-0 pt-2">
                      <CutOutDisposalSelection
                        options={typesDefaults.allValues as CUT_OUT_TYPES[]}
                        onSelect={this.handleSelect}
                        selected={this.cutOutDisposalsType}
                        enabled={typesDefaults.values}
                      />
                    </div>
                  )}
                   
                  {this.cutOutDisposalsType && (this.cutOutDisposalsType === 'CUSTOM_SQUARE' || this.cutOutDisposalsType === 'CUSTOM_ROUND') && ( 
                    <CutOutDisposalCustom />
                  )}
                  {this.cutOutDisposalsType && cutOutDisposalsEnabled && cutOutDisposalsPosition && (
                    <CheckboxPositions
                      disabled={this.disabled}
                      defaults={cutOutDisposalsPosition}
                      enabled={cutOutDisposalsEnabled}
                      id={key}
                      positionChange={this.handlePositionChange}
                      division={'quarters'}
                      className={'rc-slider-troughed'}
                    />
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  private get builtInPartsDefaults(): BuiltInPartsDefaultModel | undefined {
    return this.configuration &&
      this.configuration.defaults &&
      this.configuration.defaults.builtInParts;
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration && this.props.configuration.result;
  }

  private get requiredApronHeight(): number {
    const cutOutSpec = this.cutOutDisposalsTypeSpec;
    return this.cutOutDisposalsApronRequired && cutOutSpec && cutOutSpec.reqDimensions.apron && cutOutSpec.reqDimensions.apron.min || 0;
  }

  private get requiredDepth(): number {
    const cutOutSpec = this.cutOutDisposalsTypeSpec;
    return cutOutSpec && (cutOutSpec.reqDimensions.depth + cutOutSpec.buffer.front) || 0;
  }

  private get disabled(): boolean {
    return !isConfigurationEditable();
  }
  
  private dispatchConfiguration(props: CutOutDisposalState) {
    if (!this.configuration || !this.configuration.builtInParts) {
      return;
    }

    const builtInParts: BuiltInPartsModel = this.configuration.builtInParts;
    const data: BuiltInPartsModel = _assign({}, builtInParts, {
      cutOutDisposals: props.cutOutDisposals,
      cutOutDisposalsType: props.cutOutDisposalsType,
      cutOutDisposalsPosition: props.cutOutDisposalsPosition || null,
      cutOutDisposalsApronRequired: props.cutOutDisposalsApronRequired,
      cutOutDisposalsCustomSettings: props.cutOutDisposalsCustomSettings
    });

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {builtInParts: data}
        },
      },
      true,
    ));
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const {
      cutOutDisposalsPosition,
    } = this.state;
    const target = event.target as HTMLInputElement;
    const cutOutDisposals = target.value === 'true' && true || target.value === 'false' && false;

    const state = {
      ...this.state,
      cutOutDisposals,
      cutOutDisposalsPosition: cutOutDisposals ? cutOutDisposalsPosition : [],
      cutOutDisposalsType: null,
      cutOutDisposalsCustomSettings: null
    };

    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private handleSelect(value: CUT_OUT_TYPES | null) {
    const cutOutDisposalsPosition = this.state.cutOutDisposalsPosition;

    if (cutOutDisposalsPosition) {
      for (let i = 0; i < cutOutDisposalsPosition.length; i++) {
        cutOutDisposalsPosition[i] = false;
      }
    }

    const state = {
      ...this.state,
      cutOutDisposalsPosition: cutOutDisposalsPosition,
      cutOutDisposalsType: value,
      cutOutDisposalsApronRequired: true,
      cutOutDisposalsCustomSettings: null,
    };

    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private handlePositionChange(positions: boolean[]) {
    const state = {
      ...this.state,
      cutOutDisposalsType: this.cutOutDisposalsType, // Shouldnt be needed, but it is! Really strange bug, setState from componentDidUpdate is not working
      cutOutDisposalsPosition: positions
    };

    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private get cutOutDisposals(): BuiltInPartsModel['cutOutDisposals'] {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.cutOutDisposals ? this.configuration.builtInParts.cutOutDisposals : false;
  }

  private get hasApron(): BuiltInPartsModel['cutOutDisposals'] {
    return this.configuration &&
      this.configuration.apronBackSplash &&
      this.configuration.apronBackSplash.apron === 'with' ? true : false;
  }

  private get cutOutDisposalsType() {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.cutOutDisposalsType ? this.configuration.builtInParts.cutOutDisposalsType : null;
  }

  private get cutOutDisposalsTypeDefaults(): BuiltInPartsDefaultModel['cutOutDisposalsType'] | undefined {
    return this.configuration &&
      this.configuration.defaults &&
      this.configuration.defaults.builtInParts &&
      this.configuration.defaults.builtInParts.cutOutDisposalsType;
  }

  private get cutOutDisposalsPosition(): BuiltInPartsModel['cutOutDisposalsPosition'] | null | undefined {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.cutOutDisposalsPosition;
  }

  private get cutOutDisposalsCustomSettings(): BuiltInPartsModel['cutOutDisposalsCustomSettings'] | null | undefined {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.cutOutDisposalsCustomSettings;
  }

  private get cutOutDisposalsApronRequired(): BuiltInPartsModel['cutOutDisposalsApronRequired'] | null | undefined {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.cutOutDisposalsApronRequired;
  }

  private get cutOutDisposalsDefaults(): BuiltInPartsDefaultModel['cutOutDisposals'] | undefined {
    return this.builtInPartsDefaults &&
      this.builtInPartsDefaults.cutOutDisposals;
  }

  private get cutOutDisposalsPositionDefaults(): BuiltInPartsDefaultModel['cutOutDisposalsPosition'] | undefined {
    return this.builtInPartsDefaults &&
      this.builtInPartsDefaults.cutOutDisposalsPosition;
  }

  private get cutOutDisposalsTypeSpec(): CutOut | undefined {
    if (!this.configuration) { return; }

    const conf = new Configuration(this.configuration);
    const spec = buildCutOutSpec(this.cutOutDisposalsType, conf);

    return spec;
  }
}

export const CutOutDisposal = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration,
}))(CutOutDisposalPartial);

export default CutOutDisposal;
